import {Injectable} from '@angular/core';
import {Project} from '../../models/project';
import {Folder} from '../../models/folder';
import {ApiUserService} from '../sp-api/sp-api-user/api-user.service';
import {CrawlTrackerService} from '../sp-crawl-tracker/crawl-tracker.service';
import {Subject} from 'rxjs';
import {ApiProjectService} from '../sp-api/sp-api-project/api-project.service';
import {StatHandler} from '../../classes/data/stat-handler';
import {ApiDataService} from '../sp-api/sp-api-data/api-data.service';
import {Audience} from '../../models/audience';
import {Universe} from '../../models/universe';
import {LoggerService} from '../sp-logger/logger.service';
import {ApiAudienceService} from '../sp-api/sp-api-audience/api-audience.service';
import {TreeNode} from '../../classes/data/tree-node';
import {PathEntry} from '../../classes/data/path-entry';
import { Router } from '@angular/router';
import { SessionProviderService } from '../sp-authentication/session-provider.service';
import { ApiPlatformFoldersService, PLATFORM_FOLDERS_FLAG } from '../sp-api/sp-api-platform-folders/api-platform-folders.service';
import { FeatureFlagsService } from '../feature-flag.service';
import { isPlatformFolder, PlatformFolder } from '../../models/platform-folder';

/** A regular folder: Soprism's own, or, with `PLATFORM_FOLDERS_FLAG` on, one the Audiense platform keeps. */
export type AnyFolder = Folder | PlatformFolder;

export enum ProjectSortType {
  ALPHABETIC = 'a-z',
  CREATION_DATE = 'create-desc'
}

@Injectable({
  providedIn: 'root'
})
export class ProjectManagerService {
  private _projects: Project[] = [];

  private _openedFolder: AnyFolder;
  private _folders: AnyFolder[] = [];
  /** The platform folders were read (or failed to be) this session: the root list can exclude their projects. */
  private _platformFoldersLoaded = false;
  private _customFolders: Folder[] = [];

  private _projectCount: number = 0;
  private _currProjectPage: number = 0;

  private _chunkLoading: boolean = false;
  private projectData: Map<number, StatHandler> = new Map();
  private personaData: Map<number, StatHandler> = new Map();
  private projectSunburstData: Map<number, TreeNode<PathEntry>[]> = new Map();

  projectAndFolderTypeFiltering: string[] = ['project', 'project_shared', 'folder'];

  projectDataLoadedListener: Subject<StatHandler> = new Subject<StatHandler>();
  projectChunkListener: Subject<Project[]> = new Subject<Project[]>();
  newFolderListener: Subject<AnyFolder> = new Subject<AnyFolder>();
  projectListSearchListener: Subject<string> = new Subject<string>();
  projectTypeFilterListener: Subject<string[]> = new Subject<string[]>();
  projectTypeFilterLoading: Subject<boolean> = new Subject<boolean>();
  selectedProjectChangeListener: Subject<Project> = new Subject<Project>();
  sortType: ProjectSortType = ProjectSortType.CREATION_DATE;
  projectCreatedCount: number = 0;

  private _selectedProject: Project;
  private _selectedProjectShortcut: Project; // For project list switching, which use another simplified model

  public static readonly PROJECT_CHUNK_LIMIT: number = 15;
  private static readonly MAX_DATA_RETENTION: number = 2;
  public static readonly SEED_KEY: string = 'normalization_seed';

  // The stats API (InsightsController) returns this exact 400 plain-text body when a crawl
  // has not reached a state that allows criterion queries yet; Angular's HttpClient keeps a
  // non-JSON error body as the raw string in HttpErrorResponse.error. This happens when the client-side
  // "ready" cache (see isAudienceBaseReady/isProjectDataAccessible) is briefly out of
  // sync with the backend crawl state (e.g. right after a crawl status transition).
  // It's an expected, transient condition - not a bug - so it must not be reported to Rollbar.
  public static readonly CRAWL_NOT_PROCESSED_MESSAGE: string = 'Crawl is not processed';

  public static isCrawlNotProcessedError(error: any): boolean {
    return error?.status === 400 && error?.error === ProjectManagerService.CRAWL_NOT_PROCESSED_MESSAGE;
  }

  // Temp stuff
  // Seed param for the normalization route of Stat API
  private _seed?: number;

  constructor(
    private sessionProvider: SessionProviderService,
    private apiUser: ApiUserService,
    private apiProject: ApiProjectService,
    private apiAudience: ApiAudienceService,
    private apiData: ApiDataService,
    private crawlTracker: CrawlTrackerService,
    private logger: LoggerService,
    private router: Router,
    private platformFolders: ApiPlatformFoldersService,
    private featureFlags: FeatureFlagsService
  )
  {
    sessionProvider.sessionChangeListener.subscribe(_ => {
      this.crawlTracker.clearAll();
      this.clearProjects();
      this.clearProjectsData();
      this.clearFoldersData();
    });
    crawlTracker.crawlCriteriaDoneNotifier.subscribe(p => {
      const project = this.projects.find(proj => proj.id === p.id);
      if (!project) return;

      this.clearProjectData(project);
      this.reloadProjectInfo(project).then();
    });
    crawlTracker.crawlDoneNotifier.subscribe(p => {
      const project = this.projects.find(proj => proj.id === p.id);
      if (!project) return;

      this.clearProjectData(project);
      this.reloadProjectInfo(project).then();
    });
    crawlTracker.personaCrawlDoneNotifier.subscribe(([project, persona]: [Project, Audience]) => {
      for (const project of this.projects) {
        const foundAudience = project.audience_target.audiences_attached.find(audience => audience.id == persona.id);
        if (foundAudience) {
          this.clearProjectData(project);
          this.reloadAudienceCrawlInfo(foundAudience);
          break;
        }
      }
    });
    const storedSeed = localStorage.getItem(ProjectManagerService.SEED_KEY);
    this.seed = storedSeed ? parseInt(storedSeed) : undefined;
  }

  static getDefaultSegmentUniverse(universes: Universe[], customUniverseId?: number): Universe {
    const customSegment = universes.find(u => u.segment && u.id === customUniverseId);
    return customSegment ?? universes.find(u => u.segment_default);
  }

  addProject(project: Project) {
    if (!this._projects.find(p => p.id == project.id)) {
      this._projects.push(project);
    }

    if (!this.selectedProject){
      this.selectedProject = project;
    }
  }

  // TODO: Transform these functions as static
  public isProjectCompletelyReady(project: Project) {
    return this.isAudienceCompletelyReady(project.audience_target) &&
      this.isAudienceCompletelyReady(project.audience_bench);
  }

  public isAudienceBaseReady(audience: Audience) {
    const status = audience.status;
    const statusCheck = status && (status.actualStep > CrawlTrackerService.STEP_CRITERIA_AVAILABLE || status.state == 'done');

    return audience.latest_crawl?.criteria_ready || statusCheck;
  }

  public isAudienceCompletelyReady(audience: Audience) {
    const status = audience.status;
    const statusCheck = status && status.state == 'done';

    return (audience.latest_crawl?.criteria_ready && !audience.latest_crawl?.segment_crawling) || statusCheck;
  }

  /**
   * Check if project data is accessible for viewing.
   * This ignores segment_crawling flag - allows access to pages while segmentation is in progress.
   * Use this for detail pages that should remain accessible during segmentation crawls.
   */
  public isProjectDataAccessible(project: Project): boolean {
    return this.isAudienceDataAccessible(project.audience_target) &&
      this.isAudienceDataAccessible(project.audience_bench);
  }

  /**
   * Check if audience data is accessible for viewing.
   * Only checks criteria_ready, ignores segment_crawling.
   */
  public isAudienceDataAccessible(audience: Audience): boolean {
    const status = audience.status;
    const statusCheck = status && status.state == 'done';
    return audience.latest_crawl?.criteria_ready || statusCheck;
  }

  /**
   * Check if a segmentation crawl is currently in progress for an audience.
   * Use this for UI indicators showing segmentation status.
   */
  public isSegmentationCrawling(audience: Audience): boolean {
    return audience.latest_crawl?.segment_crawling === true;
  }

  /**
   * Check if a segmentation crawl is currently in progress for a project.
   */
  public isProjectSegmentationCrawling(project: Project): boolean {
    return this.isSegmentationCrawling(project.audience_target) ||
      this.isSegmentationCrawling(project.audience_bench);
  }

  async loadFolders(flagged: boolean = false): Promise<AnyFolder[]> {
    if (!flagged && await this.foldersOnPlatform()) {
      this._folders = await this.loadPlatformFolders();
      return this._folders;
    }

    const folders = await this.apiUser.getFolders(this.sessionProvider.activeSession.user, flagged);
    if (!flagged) {
      this._folders = folders;
    } else {
      this._customFolders = folders;
    }

    return folders;
  }

  /** Whether the user's regular folders are the ones the Audiense platform keeps. */
  foldersOnPlatform(): Promise<boolean> {
    return this.featureFlags.isEnabledForCurrentUser(PLATFORM_FOLDERS_FLAG);
  }

  /** None, logged, when the platform cannot be read: the projects still show, outside any folder. */
  private async loadPlatformFolders(): Promise<PlatformFolder[]> {
    try {
      return await this.platformFolders.getFolders();
    } catch (error) {
      this.logger.logError(error);
      return [];
    } finally {
      this._platformFoldersLoaded = true;
    }
  }

  /**
   * How to ask Soprism for a page of projects when folders live on the
   * platform: an opened folder's by id, the root list without any folder's.
   * `null` when the opened folder holds no Soprism project: nothing to ask.
   */
  private async platformProjectIds(): Promise<{projectIds?: number[], excludeProjectIds?: number[]} | null | undefined> {
    if (isPlatformFolder(this._openedFolder)) {
      const projectIds = this._openedFolder.projectIds;
      return projectIds.length > 0 ? {projectIds} : null;
    }
    // A Soprism folder (a Custom Folder) is opened by its own id: core-api would
    // let an exclude list win over `folder_id` and answer the root list instead.
    if (this._openedFolder) return undefined;
    if (!this._platformFoldersLoaded) this._folders = await this.loadPlatformFolders();
    const inFolders = new Set(this._folders.filter(isPlatformFolder).flatMap(folder => folder.projectIds));
    return {excludeProjectIds: [...inFolders].sort((a, b) => a - b)};
  }


  async loadProjectChunk(sort?: ProjectSortType, filter?: string, typeFilter?: string[]) {
    if (typeFilter && !typeFilter.includes('project') && !typeFilter.includes('project_shared')) return;

    this._chunkLoading = true;

    try {
      const onPlatform = await this.foldersOnPlatform();
      const ids = onPlatform ? await this.platformProjectIds() : undefined;
      if (ids === null) {
        this._projectCount = 0;
        this._chunkLoading = false;
        this.projectChunkListener.next([]);
        return;
      }

      const loadedPage = await this.apiUser.getProjects(this.sessionProvider.activeSession.user,
        ProjectManagerService.PROJECT_CHUNK_LIMIT,
        this._currProjectPage + 1,
        sort,
        filter,
        typeFilter,
        isPlatformFolder(this._openedFolder) ? undefined : this._openedFolder?.id,
        ids
    );
    if (this._projectCount !== loadedPage.meta.total) this._projectCount = loadedPage.meta.total;
    this._currProjectPage = loadedPage.meta.current_page;
    const projectSource = loadedPage.data;

    projectSource.forEach(p => {
      p._owner = p.user.id == this.sessionProvider.activeSession.user.id;
    });

    const projectDuplicates =  this._projects.filter(p => projectSource.find(p2 => p2.id === p.id));

    projectDuplicates.forEach(project => {
      const index = projectSource.findIndex(p => p.id === project.id);
      projectSource[index] = project;
    })

    this._projects = this._projects
      .filter(p => !projectDuplicates.find(p2 => p2.id === p.id))
      .concat(projectSource);

    this.projectChunkListener.next(projectSource);
    this.trackCrawlInfo(projectSource).catch(err => this.logger.logError(err));

      this._chunkLoading  = false;
    } catch (error) {
      if (error.status === 403) {
        this.router.navigate(['/page-access-denied']).then();
      } else {
        this.logger.logError(error);
      }
      this._chunkLoading = false;
    }
  }
  unloadProjectChunks(forceLoading: boolean = false) {
    this._chunkLoading = forceLoading || false;
    this._currProjectPage = 0;
    this._projects = [];
    this.crawlTracker.clearAll();
  }

  unloadFolders() {
    this._platformFoldersLoaded = false;
    this._folders = []
    this._customFolders = [];
  }

  reloadFolders(typeFilter?: string[]) {
    this.unloadFolders();
    if (typeFilter && !typeFilter.includes('folder') && !typeFilter.includes('folder_custom')) {
      return;
    } else {
      if(typeFilter.includes('folder')) {
        this.loadFolders().then();
      }
      if(typeFilter.includes('folder_custom')) {
        this.loadFolders(true).then();
      }
    }

  }

  reloadProjectChunks(filter?: string, typeFilter?: string[]) {
    this.unloadProjectChunks();
    this.loadProjectChunk(this.sortType, filter, typeFilter).then();
  }
  async trackCrawlInfo(projects: Project[]) {
    // Crawl tracking
    for (const project of projects) {
      if (!this._projects.find(p => p.id === project.id)) return;
      if (!this.isProjectCompletelyReady(project)) {
        await this.crawlTracker.addProject(project);
        if (!this._projects.find(p => p.id === project.id)) return;
      }
      if (project.audience_target.audiences_attached.length > 0) {
        project.audience_target.audiences_attached.forEach(persona => {
          if (!this.isAudienceCompletelyReady(persona)) {
            this.crawlTracker.addPersona(persona,10000,project);
          }
        });
      }
    }
  }

  selectFolder(folder: AnyFolder) {
    this._openedFolder = folder;
    this.unloadProjectChunks();
  }

  /**
   * Get project data from cache or api is not loaded yet
   * Stat handler keys: "criterion" & "tags"
   * @param project
   * @param normalized
   * @param refresh
   */
  getProjectData(project: Project, normalized: boolean = true, refresh: boolean = false): Promise<StatHandler> {
    return new Promise<StatHandler>((resolve, reject) => {
      if (this.isProjectCompletelyReady(project)) {
        let loadData = true;
        if (this.projectData.has(project.id) && !refresh) {
          const existingStatHandler = this.projectData.get(project.id);
          const requiredCriteriaTypes = ['tags', 'personae', 'criterion'];
          const missingCriteriaTypes = requiredCriteriaTypes.filter(x => !existingStatHandler.has(x));
          if (missingCriteriaTypes.length === 0) {
            loadData = false;
            resolve(existingStatHandler);
          }
          else {
            loadData = true;
          }
        }

        if (loadData) {
          this.apiData.getCrawledCriterion(project.audience_target, project.audience_bench, normalized, this._seed)
            .then(statHandler => {
              this.projectData.set(project.id, statHandler);
              if (this.projectData.size > ProjectManagerService.MAX_DATA_RETENTION) {
                this.projectData.delete(this.projectData.keys().next().value);
              }
              this.projectDataLoadedListener.next(statHandler);
              resolve(statHandler)
            })
            .catch(error => {
              if (ProjectManagerService.isCrawlNotProcessedError(error)) {
                this.logger.logWarning("Load project data: crawl not processed yet", 1);
              } else if (!error.status || error.status !== 401) {
                this.logger.logError("Load project data error : " + error.message, 3, error);
              }
              reject(error);
            });
        }
      } else {
        reject({"message": "The selected project is not ready yet"});
      }
    });
  }

  getProjectDataByType(
    targetAudience: Audience,
    criteriaTypes: string[],
    params: {
      normalized: boolean,
      pathsFilter?: string[],
      namesFilter?: string[],
      idsFilter?: number[],
      limit?: number,
      sortBy?: string,
      forceRefresh?: boolean
    }
  ) {
    return new Promise<StatHandler>((resolve, reject) => {
      if (!this.isAudienceBaseReady(targetAudience) || !this.isAudienceBaseReady(targetAudience.benchmark)) {
        reject({"message": "The selected audience is not available yet"});
        return;
      }
      const statHandler = this.getOrBuildStatHandler(targetAudience);
      const missingCriteriaTypes = params.forceRefresh ? criteriaTypes : criteriaTypes.filter(x => !statHandler.has(x));

      if (missingCriteriaTypes.length === 0) {
        resolve(statHandler);
      }
      else {
        this.apiData.getCrawledCriterionByType(targetAudience.id, targetAudience.benchmark.id, criteriaTypes, params)
          .then(criterionData => {
            missingCriteriaTypes.forEach(type => {
              statHandler.add(type, criterionData.filter(c => c.type === type));
            })

            this.projectData.set(targetAudience.id, statHandler);
            if (this.projectData.size > ProjectManagerService.MAX_DATA_RETENTION) {
              this.projectData.delete(this.projectData.keys().next().value);
            }
            this.projectDataLoadedListener.next(statHandler);

            resolve(statHandler)
          })
          .catch(error => {
            if (ProjectManagerService.isCrawlNotProcessedError(error)) {
              this.logger.logWarning("Load project data by type: crawl not processed yet", 1);
            } else if (!error.status || error.status !== 401) {
              this.logger.logError("Load project data error : " + error.message, 3, error);
            }
            reject(error);
          });
      }
    });
  }

  private getOrBuildStatHandler(targetAudience: Audience) {
    if (this.projectData.has(targetAudience.id)) return this.projectData.get(targetAudience.id);

    const statHandler = new StatHandler();
    this.projectData.set(targetAudience.id, statHandler);
    return statHandler;
  }

  /*
      Filling the custom type through the project manager for now
      Need to do it like this because we have to notify the listeners with the updated stat handler
      and the listener is defined in the project manager.
      Ideally, anyone depending on a stat handler should somehow observe that stat handler itself
     */
  fillCustomType(statHandler: StatHandler, type: string, typesToFill: string[]){
    statHandler.fillCustomType(type, typesToFill);

    this.projectDataLoadedListener.next(statHandler);
  }

  getAudiencePersonaData(persona: Audience): Promise<StatHandler> {
    return new Promise<StatHandler>((resolve, reject) => {
      //const personaStatusCheck = persona.status && (persona.status.actualStep > CrawlTrackerService.STEP_CRITERIA_AVAILABLE || persona.status.state == "done");
      //const personaBenchmarkStatusCheck = persona.benchmark && persona.benchmark.status && (persona.benchmark.status.actualStep > CrawlTrackerService.STEP_CRITERIA_AVAILABLE || persona.benchmark.status.state == "done");
      if (this.isAudienceBaseReady(persona) && this.isAudienceBaseReady(persona.benchmark)) {
        if (this.personaData.has(persona.id)) {
          const pData = this.personaData.get(persona.id);
          this.projectDataLoadedListener.next(pData);
          resolve(pData);
        } else {
          this.apiData.getCrawledCriterion(persona, persona.benchmark)
            .then(statHandler => {
              this.personaData.set(persona.id, statHandler);
              if (this.personaData.size > ProjectManagerService.MAX_DATA_RETENTION) {
                this.personaData.delete(this.personaData.keys().next().value);
              }
              this.projectDataLoadedListener.next(statHandler);
              resolve(statHandler);
            }).catch(error => {
              if (!error.status || error.status !== 401) {
                this.logger.logError("Audience persona data load error : " + error.message, 3, error);
              }
              reject(error)
          });
        }
      } else {
        reject({"message": "Your project is not available yet"});
      }
    })
  }
  getProjectSunburstData(project: Project, refresh: boolean = false, categoryFilter?: string): Promise<TreeNode<PathEntry>[]> {
    return new Promise<TreeNode<PathEntry>[]>((resolve, reject) => {
      if (this.isProjectCompletelyReady(project)) {
        if (this.projectSunburstData.has(project.id) && !refresh) {
          resolve(this.projectSunburstData.get(project.id));
        } else {
          this.apiData.getSunburstData(project, categoryFilter).then(tree => {
            this.projectSunburstData.set(project.id, tree);
            if (this.projectSunburstData.size > ProjectManagerService.MAX_DATA_RETENTION) {
              this.projectSunburstData.delete(this.projectSunburstData.keys().next().value);
            }
            resolve(tree);
          }).catch(err => {
            if (!err.status || err.status !== 401) {
              this.logger.logError('Sunburst data load failed', 3, err);
            }
            reject(err);
          });
        }
      }
    });
  }
  clearProjects() {
    this._projects = [];
    this._currProjectPage = 0;
    this._selectedProject = undefined;
  }
  clearProjectsData() {
    this.projectData.clear();
    this.projectSunburstData.clear();
    this.personaData.clear();
  }

  clearProjectData(p: Project) {
    this.projectData.delete(p.id);
    this.personaData.delete(p.audience_target.id);
    p.audience_target.audiences_attached.forEach(a => {
      this.personaData.delete(a.id);
    });
    this.projectSunburstData.delete(p.id);
  }

  clearProjectSunburstData(p: Project) {
    this.projectSunburstData.delete(p.id);
  }
  
  clearFoldersData() {
    this._platformFoldersLoaded = false;
    this._folders = [];
    this._customFolders = [];
  }

  async addProjectAtFirstPosition(project: Project) {
    project._owner = this.sessionProvider.activeSession.user.id === project.user.id;

    // Removing existing project with same id
    if (this._projects.find(p => p.id === project.id)) {
      this.removeLocalProject(project);
    }

    await this.crawlTracker.addProject(project);
    this._projects.unshift(project);
  }
  addFolder(folder: AnyFolder, custom: boolean) {
    if (custom) this._customFolders.push(folder as Folder);
    else this._folders.push(folder);

    this.newFolderListener.next(folder);
  }
  trackPersona(persona: Audience) {
    if (!this.crawlTracker.inPersonaeWaitingRoom(persona)) {
      this.crawlTracker.addPersona(persona,10000, this.selectedProject);
    }
  }
  removeLocalProject(project: Project) {
    this._projects = this._projects.filter(p => p.id !== project.id);
  }
  /**
   * Pseudo-caching project
   * @param id
   */
  async getProject(id: number): Promise<Project> {
    let project = this.projects.find(p => p.id === id);
    if (project) {
      //project._owner = project.user.id == this.auth.session.user.id;
      if (!project._ready) this.addProject(project);
    }
    else {
      project = await this.apiProject.getOne(id);
      project._owner = project.user.id == this.sessionProvider.activeSession.user.id;
      project.audience_target.audiences_attached.forEach(persona => {
        this.crawlTracker.addPersona(persona,10000, this.selectedProject);
      });

      this.addProject(project);
    }

    // Ensure crawl tracking is active if project is still crawling (e.g., after page reload)
    if (!this.isProjectCompletelyReady(project)) {
      await this.crawlTracker.addProject(project);
    }

    project.audience_target.benchmark = project.audience_bench;

    return project;
  }

  async reloadProjectInfo(project: Project) {
    const p = await this.apiProject.getOne(project.id);
    project.audience_target.latest_crawl = p.audience_target.latest_crawl;
    project.audience_bench.latest_crawl = p.audience_bench.latest_crawl;
    project.audience_target.universes = p.audience_target.universes;
    project.audience_bench.universes = p.audience_bench.universes;
  }
  reloadAudienceCrawlInfo(audience: Audience) {
    this.apiAudience.getOne(audience.id).then(a => {
      audience.latest_crawl = a.latest_crawl;
    });
  }
  removeFolder(folder: AnyFolder) {
    if (!folder.flag) this._folders = this._folders.filter(f => f.id !== folder.id);
    else this._customFolders = this._customFolders.filter(f => f.id !== folder.id);
  }

  get openedFolder(): AnyFolder { return this._openedFolder; }
  get folders(): AnyFolder[] { return [...this._customFolders, ...this._folders]; }
  get projects(): Project[] { return this._projects ;}
  get allProjects(): Project[] { return this.sessionProvider.activeSession ? this.sessionProvider.activeSession.user.projects : []; }
  get chunkLoading(): boolean { return this._chunkLoading; }
  get projectCount(): number { return this._projectCount; }

  get selectedProject(): Project {
    return this._selectedProject;
  }
  set selectedProject(project: Project) {
    this._selectedProject = project;
    /*if (this.projects.length == 0) this._selectedProject = project;
    else this._selectedProject = this.projects.find(p => p.id == project.id);*/

    // Setting shortcut value based on ID
    this._selectedProjectShortcut = this.allProjects.find(p => p.id == project.id);
    
    // Notify listeners about project selection change
    if (project) {
      this.selectedProjectChangeListener.next(project);
    }
  }
  get selectedProjectShortcut(): Project {
    return this._selectedProjectShortcut;
  }
  set selectedProjectShortcut(project: Project) {
    this._selectedProjectShortcut = project;
  }
  set seed(seed: number|undefined) {
    this._seed = seed;
    if (seed) localStorage.setItem(ProjectManagerService.SEED_KEY, seed.toString(10));
    else localStorage.removeItem(ProjectManagerService.SEED_KEY);
  }

  get seed() {
    return this._seed;
  }
}
